import { Collapse } from "@material-ui/core";
import { useEffect, useMemo, useRef, useState } from "react";
import { FiMapPin, FiSearch, FiUser } from "react-icons/fi";
import { MdOutlineBusiness } from "react-icons/md";
import { getAddressDetails } from "../../containers/User/AddressBook/actions";
import AddUpdateDetails from "../../containers/User/AddressBook/AddUpdateDetails";
import { getCurrentUserId } from "../../utils";
import CustomAddModal from "./CustomAddModal";

export const CounterPartySelect = ({
    value = [],
    onChange,
    counterPartyDetails,
    isMultiSelect = false,
    onCounterPartyAdded,
    allowCreate = true,
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [bankChoice, setBankChoice] = useState({});
    const dropdownRef = useRef(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [loader, setLoader] = useState(false);
    const addDetailsModalRef = useRef(null);

    const getShortName = (cp) => {
        if (!cp) return "N/A";
        return cp.type === "Individual"
            ? cp.individual_shortName ||
            `${cp.individual_firstName?.[0] || ""}${cp.individual_lastName?.[0] || ""
                }`.toUpperCase()
            : cp.organisation_shortName ||
            cp.organisation_name?.substring(0, 4).toUpperCase() ||
            "ORG";
    };

    const getFullName = (cp) => {
        return !cp
            ? "Unknown"
            : cp.type === "Individual"
                ? `${cp.individual_title || ""} ${cp.individual_firstName || ""} ${cp.individual_middleName || ""} ${cp.individual_lastName || cp.fullName || ""}`.trim()
                : cp.organisation_fullName || cp.organisation_name || cp.organisation_firstName || cp.organisation_lastName || cp.fullName || "Unknown Organization";
    };

    const getAddress = (cp) => {
        if (!cp) return "Address not available";
        if (cp.type === "Individual") {
            const parts = [
                cp.individual_homeAddress,
                cp.individual_personal_city,
                cp.individual_personal_state,
                cp.individual_personal_country,
            ].filter(Boolean);
            return parts.length
                ? parts.join(", ")
                : cp.individual_location || "Address not available";
        }
        const parts = [
            cp.organisation_officeAddress,
            cp.organisation_city,
            cp.organisation_state,
            cp.organisation_country,
        ].filter(Boolean);
        return parts.length ? parts.join(", ") : "Address not available";
    };

    const getRefNo = (cp) => cp?._id?.slice(-6).toUpperCase() || "N/A";

    const getSelectedBankAccountNumber = (cp, cpDetail) => {
        if (!cpDetail) return "N/A";

        const choiceType =
            bankChoice[cp.id] ||
            (cpDetail?.type === "Individual" ? "individual" : "organisation");

        if (choiceType === "individual") {
            return cpDetail.individual_bankAccountNumber || "N/A";
        } else {
            return cpDetail.organisation_bankAccountNumber || "N/A";
        }
    };

    const availableCounterParties = useMemo(() => {
        const allCounterParties = (counterPartyDetails || []).map((d) => ({
            id: d._id,
            name: d.type === "Individual"
                ? `${d.individual_firstName || ""} ${d.individual_lastName || ""}`.trim()
                : d.organisation_fullName || d.fullName || "",
            detail: d,
        }));

        if (!searchTerm) return allCounterParties;

        const q = searchTerm.toLowerCase();
        return allCounterParties.filter((cp) => {
            const d = cp.detail;
            const fullName = getFullName(d).toLowerCase();
            const shortName = getShortName(d).toLowerCase();
            const address = getAddress(d).toLowerCase();
            const accountNumber = getSelectedBankAccountNumber(cp, d).toLowerCase();

            return (
                fullName.includes(q) ||
                shortName.includes(q) ||
                address.includes(q) ||
                accountNumber.includes(q) ||
                cp.name?.toLowerCase().includes(q)
            );
        });
    }, [counterPartyDetails, searchTerm, bankChoice]);

    const CounterPartyIcon = ({ type }) =>
        type === "Individual" ? (
            <FiUser className="w-3.5 h-3.5 text-sky-600" />
        ) : (
            <MdOutlineBusiness className="w-3.5 h-3.5 text-emerald-600" />
        );

    const handleSelectCounterparty = (cp, cpDetail) => {
        if (typeof onChange !== "function") return;

        if (isMultiSelect) {
            let newValue = Array.isArray(value) ? [...value] : [];
            if (newValue.includes(cp.id)) {
                newValue = newValue.filter((id) => id !== cp.id);
            } else {
                newValue.push(cp.id);
            }
            onChange({ target: { value: newValue } });
        } else {
            onChange({ target: { value: cp.id } });
        }
    };

    return (
        <div
            ref={dropdownRef}
            className="z-[9999] bg-white border border-slate-300 rounded-none overflow-hidden"
        >
            {/* Search and Add Button */}
            <div className="p-1.5 border-b border-slate-200 bg-slate-50 sticky top-0 flex items-center gap-2">
                <div className="relative flex-grow">
                    <input
                        type="text"
                        placeholder="Search by name, short name, bank account, address..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-8 pr-3 h-6 border border-slate-300 rounded-none text-[11px] font-medium focus:outline-none focus:border-slate-600 bg-white"
                        autoFocus
                    />
                    <FiSearch className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                </div>
                {allowCreate && <button
                    type="button"
                    onClick={() => setIsAddModalOpen(true)}
                    className="flex-shrink-0 bg-[#313C4C] text-white hover:bg-[#1e2632] inline-flex items-center px-2.5 h-6 text-[11px] font-medium rounded-none shadow-none focus:outline-none transition-colors duration-200 gap-1"
                >
                    <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                        />
                    </svg>
                    <span>Add</span>
                </button>}
            </div>

            {/* Phones: keep the 12-column grid at a readable width and scroll it sideways */}
            <div className="overflow-x-auto scroll-thin">
            <div className="min-w-[640px]">
            {/* Table Header */}
            <div className="bg-[#e2e8f0] border-b border-slate-300 px-3 py-0.5">
                <div className="grid grid-cols-12 gap-2 text-[11px] font-medium text-[#1e293b] capitalize tracking-wider whitespace-nowrap">
                    <div className="col-span-1 text-center">Select</div>
                    <div className="col-span-1">Type</div>
                    <div className="col-span-2">Short Name</div>
                    <div className="col-span-2">Full Name</div>
                    <div className="col-span-2">Bank Account</div>
                    <div className="col-span-3">Address</div>
                    <div className="col-span-1">Ref</div>
                </div>
            </div>

            {/* Results Table */}
            <div className="max-h-[400px] overflow-y-auto scroll-thin">
                {!availableCounterParties.length ? (
                    <div className="px-4 py-4 text-slate-500 text-[11px] text-center">
                        {searchTerm
                            ? "No counterparties found matching your search."
                            : (
                                <div>
                                    <p>No counterparties available.</p>
                                    {allowCreate && <button
                                        type="button"
                                        onClick={() => setIsAddModalOpen(true)}
                                        className="border mt-2 border-slate-300 bg-[#313C4C] text-white inline-flex items-center px-3 h-7 text-[11px] font-medium rounded-none hover:bg-[#1e2632] focus:outline-none transition-colors duration-200"
                                    >
                                        <svg
                                            className="w-3.5 h-3.5 mr-1"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 4v16m8-8H4"
                                            />
                                        </svg>
                                        Create New Counter Party
                                    </button>}
                                </div>
                            )}
                    </div>
                ) : (
                    availableCounterParties.map((cp) => {
                        const d = cp.detail;
                        if (!d) return null;

                        const shortName = getShortName(d);
                        const fullName = getFullName(d);
                        const address = getAddress(d);
                        const refNo = getRefNo(d);
                        const accountNumber = getSelectedBankAccountNumber(cp, d);

                        if (!bankChoice[cp.id]) {
                            setBankChoice((s) => ({
                                ...s,
                                [cp.id]: d.type === "Individual" ? "individual" : "organisation",
                            }));
                        }

                        const isSelected = isMultiSelect
                            ? value.includes(cp.id)
                            : value === cp.id;

                        return (
                            <div key={cp.id} className="border-b border-slate-200">
                                <div
                                    onClick={() => handleSelectCounterparty(cp, d)}
                                    className={`px-3 py-0.5 hover:bg-amber-50/50 cursor-pointer transition-colors ${isSelected ? "bg-[#ffffd6]" : ""}`}
                                >
                                    <div className="grid grid-cols-12 gap-2 items-center text-[11px] font-medium text-slate-900 cursor-pointer">
                                        {/* Select Input */}
                                        <div className="col-span-1 text-center">
                                            {isMultiSelect ? (
                                                <input
                                                    type="checkbox"
                                                    className="w-3.5 h-3.5 accent-[#313C4C] cursor-pointer"
                                                    checked={isSelected}
                                                    onChange={() => handleSelectCounterparty(cp, d)}
                                                />
                                            ) : (
                                                <input
                                                    type="radio"
                                                    name="counterparty-single"
                                                    className="w-3.5 h-3.5 accent-[#313C4C] cursor-pointer"
                                                    checked={isSelected}
                                                    onChange={() => handleSelectCounterparty(cp, d)}
                                                />
                                            )}
                                        </div>

                                        {/* Type Icon */}
                                        <div className="col-span-1 flex items-center gap-1">
                                            <span>{d.type.slice(0, 1)}</span>
                                            <CounterPartyIcon type={d.type} />
                                        </div>

                                        {/* Short Name */}
                                        <div className="col-span-2">
                                            <span className="font-medium text-[11px]">
                                                {shortName}
                                            </span>
                                        </div>

                                        {/* Full Name */}
                                        <div className="col-span-2 truncate font-medium text-slate-900">
                                            {fullName}
                                        </div>

                                        {/* Bank Account Info */}
                                        <div className="col-span-2 font-mono text-[11px] font-medium text-slate-800">
                                            {accountNumber}
                                        </div>

                                        {/* Address */}
                                        <div className="col-span-3 text-slate-600 text-[11px] truncate flex items-center gap-1">
                                            <FiMapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                            <span className="truncate">{address}</span>
                                        </div>

                                        {/* Ref */}
                                        <div className="col-span-1">
                                            <span className="font-medium text-slate-700 text-[11px]">
                                                #{refNo}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
            </div>
            </div>
            {allowCreate && isAddModalOpen && (
                <CustomAddModal
                    open={isAddModalOpen}
                    title="Add details"
                    saveLabel="Create"
                    onSubmitConfirm={() => {
                        if (addDetailsModalRef.current?.onSubmitQuery) {
                            addDetailsModalRef.current.onSubmitQuery();
                        }
                    }}
                    handleClose={() => setIsAddModalOpen(false)}
                    maxWidth="lg"
                >
                    <AddUpdateDetails
                        type="user"
                        ref={addDetailsModalRef}
                        setLoader={setLoader}
                        onCloseModal={() => setIsAddModalOpen(false)}
                        getAllSavedResults={() => {
                            if (typeof onCounterPartyAdded === "function") {
                                onCounterPartyAdded();
                            }
                        }}
                        singleResults={{}}
                        isEdit={false}
                        isReadOnly={false}
                    />
                </CustomAddModal>
            )}
        </div>
    );
};


const CounterPartySelection = ({
    portList = [],
    updateCounterPartyAtPort,
    handleDemurrageChange,
    handleDespatchChange,
    isMultiSelect = false,
    isReadOnly = false,
}) => {
    const [openPortIndex, setOpenPortIndex] = useState(null);
    const [counterPartyDetails, setCounterPartyDetails] = useState([]);
    const [isExpanded, setIsExpanded] = useState(true);

    const fetchCounterPartyDetails = async () => {
        const userId = getCurrentUserId();
        try {
            const userRes = await getAddressDetails({
                userId,
                addressBookType: "user",
                perPage: 100,
            });

            setCounterPartyDetails(userRes?.data?.list);
        } catch (error) {
            console.error("Failed to fetch counter party details:", error);
            setCounterPartyDetails([]);
        }
    };

    useEffect(() => {
        fetchCounterPartyDetails();
    }, []);

    const handleCounterPartyChange = (portId, newValue) => {
        let updated = [];

        if (Array.isArray(newValue)) {
            updated = newValue.map((id) => {
                const cp = counterPartyDetails.find((c) => c._id === id);
                return {
                    id,
                    name: cp?.type === "Individual"
                        ? `${cp.individual_firstName || ""} ${cp.individual_lastName || ""}`.trim()
                        : cp?.organisation_fullName || "Unknown",
                    type: cp?.type,
                };
            });
        } else {
            const cp = counterPartyDetails.find((c) => c._id === newValue);
            updated = cp
                ? [
                    {
                        id: cp._id,
                        name: cp?.type === "Individual"
                            ? `${cp.individual_firstName || ""} ${cp.individual_lastName || ""}`.trim()
                            : cp?.organisation_fullName || "Unknown",
                        type: cp?.type,
                    },
                ]
                : [];
        }

        updateCounterPartyAtPort(portId, updated);
    };

    const onDemurrageChange = (portId, newValue) => {
        handleDemurrageChange(portId, newValue);
    };

    const onDespatchChange = (portId, newValue) => {
        handleDespatchChange(portId, newValue);
    };

    const filteredPorts = useMemo(() => {
        return portList?.filter((port) => port?.operation?.toLowerCase() !== "commence") || [];
    }, [portList]);

    return (
        <div className="w-full font-sans border border-slate-300 rounded-none overflow-hidden mb-0">
            {/* Collapsible Header */}
            <div
                className="flex items-center justify-between cursor-pointer bg-slate-100 px-2.5 py-1 hover:bg-slate-200 transition-colors"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <span className="text-[11px] font-medium text-slate-900 select-none capitalize tracking-wider">
                    Select Counter Parties for each port <span className="text-rose-500">*</span>
                </span>
                <svg
                    className={`w-3.5 h-3.5 text-slate-600 transition-transform duration-300 ${isExpanded ? "transform rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </div>

            {/* Collapsible Content */}
            <Collapse in={isExpanded}>
                <div className="overflow-x-auto w-full border-t border-slate-300">
                    <table className="w-full min-w-[600px] text-left border-collapse whitespace-nowrap table-fixed">
                        <thead>
                            <tr className="bg-[#f0f2f5]">
                                <th className="px-1.5 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-medium text-slate-900 bg-[#f0f2f5] capitalize tracking-wider align-middle w-[15%]">Operation</th>
                                <th className="px-1.5 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-medium text-slate-900 bg-[#f0f2f5] capitalize tracking-wider align-middle w-[20%]">Port</th>
                                <th className="px-1.5 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-medium text-slate-900 bg-[#f0f2f5] capitalize tracking-wider align-middle w-[12%]">Cargo Qty</th>
                                <th className="px-1.5 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-medium text-slate-900 bg-[#f0f2f5] capitalize tracking-wider align-middle w-[12%]">Rate</th>
                                <th className="px-1.5 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-medium text-slate-900 bg-[#f0f2f5] capitalize tracking-wider align-middle w-[13%]">Demurrage</th>
                                <th className="px-1.5 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-medium text-slate-900 bg-[#f0f2f5] capitalize tracking-wider align-middle w-[13%]">Despatch</th>
                                <th className="px-1.5 py-0.5 text-left border-b border-slate-300 text-[11px] font-medium text-slate-900 bg-[#f0f2f5] capitalize tracking-wider align-middle w-[15%]">Counter Parties</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPorts.map((port, portIndex) => {
                                const isPortDisabled = isReadOnly || (port?.operation?.toLowerCase() !== "discharging" && port?.operation?.toLowerCase() !== "loading");
                                const isLast = portIndex === filteredPorts.length - 1;
                                return (
                                    <tr
                                        key={portIndex}
                                        className="hover:bg-[#ffffcc]/60 transition-colors group"
                                    >
                                        <td className={`border-r ${!isLast ? "border-b" : ""} border-slate-300 px-1.5 py-0 text-left align-middle text-[11px] font-medium text-slate-900 bg-white`}>
                                            {port.operation || "—"}
                                        </td>
                                        <td className={`border-r ${!isLast ? "border-b" : ""} border-slate-300 px-1.5 py-0 text-left whitespace-nowrap overflow-hidden text-ellipsis align-middle text-[11px] font-medium text-slate-900 bg-white`}>
                                            {port.port ? port.port : <span className="text-slate-400">—</span>}
                                        </td>
                                        <td className={`border-r ${!isLast ? "border-b" : ""} border-slate-300 px-1.5 py-0 text-left align-middle text-[11px] font-medium text-slate-900 bg-white`}>
                                            {port.cargoQty !== undefined && port.cargoQty !== null && port.cargoQty !== "" ? port.cargoQty : 0}
                                        </td>
                                        <td className={`border-r ${!isLast ? "border-b" : ""} border-slate-300 px-1.5 py-0 text-left align-middle text-[11px] font-medium text-slate-900 bg-white`}>
                                            {port.rate !== undefined && port.rate !== null && port.rate !== "" ? port.rate : 0}
                                        </td>
                                        <td className={`border-r ${!isLast ? "border-b" : ""} border-slate-300 p-0 align-middle ${isPortDisabled ? "bg-slate-200/50" : "bg-[#ffffd6]"}`}>
                                            <input
                                                type="number"
                                                disabled={isPortDisabled}
                                                value={port.demurrage || 0}
                                                onChange={(e) => onDemurrageChange(port._id, e.target.value)}
                                                className="w-full h-full min-h-[18px] text-[11px] px-1.5 py-0 m-0 box-border block outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 bg-transparent text-slate-900 disabled:bg-slate-200/50 disabled:text-slate-500 disabled:cursor-not-allowed rounded-none border-none placeholder:text-gray-400 font-medium text-left"
                                            />
                                        </td>
                                        <td className={`border-r ${!isLast ? "border-b" : ""} border-slate-300 p-0 align-middle ${isPortDisabled ? "bg-slate-200/50" : "bg-[#ffffd6]"}`}>
                                            <input
                                                type="number"
                                                value={port.despatch || 0}
                                                disabled={isPortDisabled}
                                                onChange={(e) => onDespatchChange(port._id, e.target.value)}
                                                className="w-full h-full min-h-[18px] text-[11px] px-1.5 py-0 m-0 box-border block outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 bg-transparent text-slate-900 disabled:bg-slate-200/50 disabled:text-slate-500 disabled:cursor-not-allowed rounded-none border-none placeholder:text-gray-400 font-medium text-left"
                                            />
                                        </td>
                                        <td className={`${!isLast ? "border-b" : ""} border-slate-300 p-0 align-middle ${isPortDisabled ? "bg-slate-200/50" : "bg-[#ffffd6]"}`}>
                                            <button
                                                disabled={isPortDisabled}
                                                className={`w-full h-full min-h-[18px] text-[11px] font-medium px-1.5 py-0 bg-transparent border-none outline-none rounded-none text-left flex items-center truncate transition-colors ${
                                                    isPortDisabled
                                                        ? "text-slate-500 cursor-not-allowed no-underline"
                                                        : "hover:bg-[#fff9b3] text-slate-900 underline cursor-pointer"
                                                }`}
                                                onClick={() => {
                                                    if (isPortDisabled) return;
                                                    const realIndex = portList.findIndex(p => p._id === port._id);
                                                    setOpenPortIndex(realIndex);
                                                }}
                                                type="button"
                                            >
                                                {port.counterParties?.length
                                                    ? `${port.counterParties.length} Selected`
                                                    : "Select Parties"}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Collapse>

            {/* Modal with CounterPartySelect */}
            {openPortIndex !== null && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-none shadow-xl w-full max-w-4xl max-h-full overflow-y-auto p-1.5 border border-slate-300 animate-in fade-in duration-200">
                        <div className="flex items-center justify-between mb-2 pb-1 border-b border-slate-200">
                            <h2 className="text-[14px] font-medium text-[#313C4C]">
                                Manage Counter Parties – {portList[openPortIndex]?.port}
                            </h2>
                            <button
                                className="text-slate-400 hover:text-rose-500 rounded-none transition p-1 text-xs"
                                onClick={() => setOpenPortIndex(null)}
                                aria-label="Close"
                                type="button"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="py-0">
                            <CounterPartySelect
                                value={
                                    isMultiSelect
                                        ? portList[openPortIndex]?.counterParties?.map((cp) => cp.id) || []
                                        : portList[openPortIndex]?.counterParties?.[0]?.id || ""
                                }
                                onChange={(e) => handleCounterPartyChange(portList[openPortIndex]?._id, e.target.value)}
                                counterPartyDetails={counterPartyDetails}
                                isMultiSelect={true}
                                onCounterPartyAdded={fetchCounterPartyDetails}
                            />
                        </div>

                        {/* Footer */}
                        <div className="flex justify-end mt-3">
                            <button
                                type="button"
                                className="px-3 h-7 text-[11px] font-medium rounded-none bg-[#313C4C] text-white hover:bg-[#1e2632] transition"
                                onClick={() => setOpenPortIndex(null)}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CounterPartySelection;
